import { AlertCircle, Music, Sliders } from "lucide-react";
import { useCallback, useState } from "react";
import { EffectType, useAudioSettings } from "../../hooks/useAudioSettings";
import { Slider } from "../Slider";

export const EqualizerSettings = () => {
  const [selectedEffect, setSelectedEffect] = useState<EffectType>("lowpass");
  const [settingsChanged, setSettingsChanged] = useState(false);

  const handleAudioSettingsChange = useCallback(() => {
    console.log("Audio settings changed");
    setSettingsChanged(true);
    setTimeout(() => setSettingsChanged(false), 2000); // Reset after 2 seconds
  }, []);

  const { equalizer, updateEqualizer, presets, setPreset, effects, updateEffect, error } =
    useAudioSettings(handleAudioSettingsChange);

  const frequencyLabels = ["32Hz", "64Hz", "125Hz", "250Hz", "500Hz", "1kHz", "2kHz", "4kHz", "8kHz", "16kHz"];

  const handleBandChange = (index: number, newValue: number) => {
    const newBands = [...equalizer.bands];
    newBands[index] = newValue;
    updateEqualizer({ ...equalizer, bands: newBands });
  };

  const handleEffectChange = (type: EffectType, enabled: boolean, value: number) => {
    updateEffect({ type, enabled, value });
  };

  const renderEffectControl = (effect: { type: EffectType; enabled: boolean; value: number }) => {
    let label = "";
    let min = 0;
    let max = 1;
    let step = 0.01;

    switch (effect.type) {
      case "lowpass":
      case "highpass":
        label = "Frequency";
        max = 22050;
        step = 1;
        break;
      case "reverb":
        label = "Decay";
        break;
      case "distortion":
        label = "Amount";
        max = 100;
        step = 1;
        break;
      case "compression":
        label = "Threshold";
        min = -100;
        max = 0;
        step = 1;
        break;
      case "delay":
        label = "Delay Time";
        max = 5;
        break;
    }

    return (
      <div key={effect.type} className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-sm text-zinc-400">{effect.type.charAt(0).toUpperCase() + effect.type.slice(1)}</span>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={effect.enabled}
              onChange={(e) => handleEffectChange(effect.type, e.target.checked, effect.value)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-zinc-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-yellow-500"></div>
          </label>
        </div>
        <div className="flex items-center gap-4">
          <span className="w-24 text-sm text-zinc-400">{label}</span>
          <Slider
            min={min}
            max={max}
            step={step}
            value={effect.value}
            onChange={(newValue) => handleEffectChange(effect.type, effect.enabled, newValue)}
            className="flex-1"
          />
          <span className="w-12 text-right text-sm text-zinc-400">{effect.value.toFixed(2)}</span>
        </div>
      </div>
    );
  };

  return (
    <div className="p-6 space-y-8">
      {error && (
        <div className="bg-red-500 text-white p-4 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          <span>{error}</span>
        </div>
      )}

      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <Sliders className="w-5 h-5" />
          Enable Equalizer
        </h3>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={equalizer.enabled}
            onChange={(e) => updateEqualizer({ ...equalizer, enabled: e.target.checked })}
            className="sr-only peer"
          />
          <div className="w-11 h-6 bg-zinc-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-yellow-500"></div>
        </label>
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Music className="w-5 h-5" />
          Presets
        </h3>
        <div className="grid grid-cols-2 gap-2 mb-6">
          {presets.map((preset) => (
            <button
              key={preset.name}
              onClick={() => setPreset(preset.name)}
              className="px-4 py-2 text-center bg-zinc-800 hover:bg-zinc-700 rounded-lg transition"
            >
              {preset.name}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-6 px-2">
        {equalizer.bands.map((value, index) => (
          <div key={index} className="flex items-center gap-4">
            <span className="w-12 text-sm text-zinc-400">{frequencyLabels[index]}</span>
            <Slider
              min={-12}
              max={12}
              value={value}
              onChange={(newValue) => handleBandChange(index, newValue)}
              className="flex-1"
            />
            <span className="w-8 text-right text-sm text-zinc-400">{value.toFixed(1)}dB</span>
          </div>
        ))}
      </div>

      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Audio Effects</h3>
        <div className="flex flex-wrap gap-2 mb-4">
          {effects.map((effect) => (
            <button
              key={effect.type}
              onClick={() => setSelectedEffect(effect.type)}
              className={`px-3 py-1 rounded-full text-sm ${
                selectedEffect === effect.type ? "bg-yellow-500 text-black" : "bg-zinc-700 text-white"
              } ${effect.enabled ? "border-2 border-green-500" : ""}`}
            >
              {effect.type.charAt(0).toUpperCase() + effect.type.slice(1)}
            </button>
          ))}
        </div>
        {effects.map((effect) => effect.type === selectedEffect && renderEffectControl(effect))}
      </div>

      {settingsChanged && (
        <div className="fixed bottom-4 right-4 bg-green-500 text-white px-4 py-2 rounded-full flex items-center gap-2">
          <span>Applying changes...</span>
        </div>
      )}
    </div>
  );
};
